/** @forge-header
 * @id          kalshiApiIndex
 * @role        Barrel re-export + client factory | 26 import sites resolve here
 * @pattern     barrel
 * @purity      api
 * @units       mixed
 * @upstream    kalshiApi/client:initializeAuth | kalshiApi/types:KalshiApiClient
 *              | kalshiApi/markets:* | kalshiApi/orderbook:* | kalshiApi/orders:* | kalshiApi/positions:*
 * @downstream  useDefenseData | useMarketDetailData | useSportsStream | ~26 import sites
 * @exports     createKalshiClient:fn | KalshiOrder:type | KalshiFill:type | KalshiPosition:type | KalshiApiClient:type | isCorsError:fn | normalizeKalshiPositions:fn
 * @test        untested (integration-level)
 *
 * @invariant   price-units | yes_price/no_price are CENTS (0..100) | average_price is FRACTIONAL DOLLARS (0..1)
 */

import type { Environment } from '../../types';
import { initializeAuth, initializeMtAuth } from './client';
import type { KalshiApiClient } from './types';
import { getSeries, getEvents, getMarkets, getMarket, getEvent } from './markets';
import { getOrderbook, getCandlesticks, getMarketTrades } from './orderbook';
import { getOpenOrders, placeOrder, decreaseOrder, cancelOrder, cancelAllOrders } from './orders';
import { getPositions, getFilledOrders } from './positions';

// Re-export all public types
export type {
  KalshiOrder,
  KalshiOrderResponse,
  KalshiOrdersResponse,
  KalshiFill,
  KalshiTrade,
  KalshiPosition,
  KalshiCandlestick,
  KalshiApiClient,
} from './types';

// Re-export utilities
export { isCorsError } from './client';
export { normalizeKalshiPositions } from './positions';

/**
 * Initialize API client with credentials
 */
export async function createKalshiClient(
  accessKeyId: string,
  privateKeyPem: string,
  environment: Environment,
  useRelay: boolean = true
): Promise<KalshiApiClient> {
  try {
    await initializeAuth(accessKeyId, privateKeyPem, environment, useRelay);
  } catch (error) {
    console.error('kalshiApi: Error in createKalshiClient:', error);
    throw error;
  }

  return {
    getSeries,
    getEvents,
    getMarkets,
    getMarket,
    getOrderbook,
    getCandlesticks,
    getEvent,
    getOpenOrders,
    getFilledOrders,
    getPositions,
    getMarketTrades,
    placeOrder,
    decreaseOrder,
    cancelOrder,
    cancelAllOrders,
  };
}

/**
 * Initialize MT API client that delegates signing to the relay.
 */
export async function createMtKalshiClient(
  accessToken: string,
  environment: Environment
): Promise<KalshiApiClient> {
  try {
    await initializeMtAuth(accessToken, environment);
  } catch (error) {
    console.error('kalshiApi: Error in createMtKalshiClient:', error);
    throw error;
  }

  return {
    getSeries,
    getEvents,
    getMarkets,
    getMarket,
    getOrderbook,
    getCandlesticks,
    getEvent,
    getOpenOrders,
    getFilledOrders,
    getPositions,
    getMarketTrades,
    placeOrder,
    decreaseOrder,
    cancelOrder,
    cancelAllOrders,
  };
}
